import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import { Grid } from "@mui/material";
import { useState } from "react";

import MUIModal from "../../shared/MUIModal";
import { MUIStyled } from "../../shared/MUIStyled";
import SABox from "../../shared/SABox";
import SAButton from "../../shared/SAButton";
import LazyImage from "../../shared/SAImageLoader";
import SAStack from "../../shared/SAStack";
import SATruncateWithEllipsis from "../../shared/SATruncateWithEllipsis";
import SATypography from "../../shared/SATypography";
import SATarget from "../../shared/table-components/SATarget";

const ModalInfoBox = MUIStyled(SABox)({
  border: "1px solid #e0e0e0",
  padding: 8,
  marginTop: 8,
  display: "flex",
  alignItems: "center",
  color: "#5a5a5aff",
});

const HeaderBox = MUIStyled(SABox)(({ theme }) => ({
  background: "#f5f5f5",
  padding: theme.spacing(1),
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const HeaderInnerBox = MUIStyled(SABox)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
});

const StatusBox = MUIStyled(SABox)(({ theme }) => ({
  minWidth: 80,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
  gap: theme.spacing(1),
  border: "1px solid #9e9a93",
}));

const ImageStack = MUIStyled(SAStack)(({ theme }) => ({
  width: "100%",
  overflowX: "auto",
  paddingBottom: theme.spacing(1),
}));

const ImageBox = MUIStyled(SABox)({
  width: 100,
  height: 100,
  background: "#bbb",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  borderRadius: 8,
  flex: "0 0 auto",
  cursor: "zoom-in",
});

const RejectionTitle = MUIStyled(SATypography)(({ theme }) => ({
  marginTop: theme.spacing(1),
  fontSize: 18,
  fontWeight: 600,
}));

const CommentBox = MUIStyled(SABox)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  alignItems: "center",
  marginTop: theme.spacing(1),
}));

const CommentInputBox = MUIStyled(SABox)({
  border: "1px solid #e0e0e0",
  padding: "5px",
  display: "flex",
  width: "100%",
  alignItems: "center",
  color: "#686868",
});

const AttachmentStack = MUIStyled(SAStack)({
  overflowX: "auto",
  maxWidth: 550,
});

const AttachmentBox = MUIStyled(SABox)(({ theme }) => ({
  width: 80,
  height: 80,
  borderRadius: theme.spacing(2),
  overflow: "hidden",
  border: "1px solid #e0e0e0",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  marginRight: theme.spacing(1),
  flex: "0 0 auto",
}));

const DividerBox = MUIStyled(SABox)({
  borderBottom: "1px solid #e0e0e0",
});

const ManufactureProductDetailsModal = ({
  open,
  handleClose,
  t,
  product,
  productQuantity,
  setZoomImg,
}) => {
  const [showMoreRejected, setShowMoreRejected] = useState(false);

  return (
    <MUIModal
      open={open}
      maxWidth="sm"
      handleClose={handleClose}
      content={
        <SABox p={3}>
          <HeaderBox>
            <HeaderInnerBox>
              <SATypography fontWeight={600}>
                {t("requested-quantity")}
                {" : "}
              </SATypography>
              <SATypography ml={1}>
                {+product?.quantity} {t("pieces")}
              </SATypography>
            </HeaderInnerBox>
            {
              <SAStack direction="row" gap={2}>
                <StatusBox>
                  {productQuantity?.admin_inspection_status === "APPROVED" ||
                  productQuantity?.admin_inspection_status ===
                    "ADMIN_APPROVED" ? (
                    <>
                      <CheckOutlinedIcon /> {t("approved")}
                    </>
                  ) : productQuantity?.admin_inspection_status ===
                    "REJECTED" ? (
                    t("rejected")
                  ) : (
                    t("pending")
                  )}
                </StatusBox>
              </SAStack>
            }
          </HeaderBox>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SATypography fontWeight={600}>{t("product-name")}</SATypography>
              <ModalInfoBox>
                <SATruncateWithEllipsis text={product?.name} maxLength={65} />
              </ModalInfoBox>
            </Grid>
            <Grid item xs={6}>
              <SATypography fontWeight={600}>{t("product-type")}</SATypography>
              <ModalInfoBox>
                <SATypography>{product?.type}</SATypography>
              </ModalInfoBox>
            </Grid>
            <Grid item xs={6}>
              <SATypography fontWeight={600}>
                {t("product-weight")}
              </SATypography>
              <ModalInfoBox>
                <SATarget value={product?.weight} />
              </ModalInfoBox>
            </Grid>
            <Grid item xs={12}>
              <SATypography fontWeight={600}>{t("description")}</SATypography>
              <ModalInfoBox>
                <SATruncateWithEllipsis
                  text={product?.description}
                  maxLength={65}
                />
              </ModalInfoBox>
            </Grid>
            <Grid item xs={12}>
              <SATypography fontWeight={600}>{t("design-sample")}</SATypography>
              <ImageStack direction="row" gap={2} mt={1}>
                {(product?.design_images || []).map((img, i) => (
                  <ImageBox key={i} onClick={() => setZoomImg(img)}>
                    {img && (
                      <img
                        src={img}
                        alt={`design-${i}`}
                        style={{
                          width: 80,
                          height: 80,
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    )}
                  </ImageBox>
                ))}
              </ImageStack>
            </Grid>
            <Grid item xs={12}>
              <SATypography fontWeight={600}>
                {t("manufacturer-design-sample")}
              </SATypography>
              <ImageStack direction="row" gap={2} mt={1}>
                {(product?.manufacturerAttachments || [])?.map((img, i) => (
                  <ImageBox key={i} onClick={() => setZoomImg(img)}>
                    {img && (
                      <img
                        src={img}
                        alt={`design-${i}`}
                        style={{
                          width: 80,
                          height: 80,
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    )}
                  </ImageBox>
                ))}
              </ImageStack>
            </Grid>
          </Grid>

          {!!productQuantity?.rejected_inspected_products?.length && (
            <>
              {/* Show first rejected detail always */}
              <SAStack>
                <SABox>
                  <RejectionTitle>{t("rejection-details")}</RejectionTitle>
                </SABox>
                <SABox>
                  <CommentBox>
                    <SATypography>
                      {t("comment")}
                      {":"}
                    </SATypography>
                    <CommentInputBox>
                      <SATruncateWithEllipsis
                        text={
                          productQuantity?.rejected_inspected_products[0]
                            ?.reason
                        }
                        maxLength={60}
                      />
                    </CommentInputBox>
                  </CommentBox>
                  <AttachmentStack direction="row" spacing={1} mt={2}>
                    {productQuantity?.rejected_inspected_products[0]
                      ?.inspection_rejection_attachments?.length &&
                      productQuantity?.rejected_inspected_products[0]?.inspection_rejection_attachments?.map(
                        (src, idx) => (
                          <AttachmentBox key={idx}>
                            <LazyImage
                              src={src?.file?.url}
                              className="main-img"
                              previewImage={true}
                            />
                          </AttachmentBox>
                        ),
                      )}
                  </AttachmentStack>
                </SABox>
              </SAStack>
              {/* Show More button */}
              {productQuantity?.rejected_inspected_products?.length > 1 && (
                <SABox mt={2} textAlign="center">
                  <SAButton
                    variant="text"
                    onClick={() => setShowMoreRejected((prev) => !prev)}
                  >
                    {showMoreRejected ? t("show-less") : t("show-more")}
                  </SAButton>
                </SABox>
              )}
              {/* Collapsible section for other rejected details */}
              {showMoreRejected && (
                <SABox mt={2}>
                  {productQuantity?.rejected_inspected_products
                    .slice(1)
                    .map((rejectedDetails, index) => (
                      <>
                        <SABox key={index}>
                          <SABox>
                            <SATypography>
                              {t("rejection-details")}
                            </SATypography>
                          </SABox>
                          <SABox>
                            <CommentBox>
                              <SATypography>
                                {t("comment")}
                                {":"}
                              </SATypography>
                              <CommentInputBox>
                                <SATruncateWithEllipsis
                                  text={rejectedDetails?.reason}
                                  maxLength={60}
                                />
                              </CommentInputBox>
                            </CommentBox>
                            <AttachmentStack direction="row" spacing={1} mt={2}>
                              {rejectedDetails?.inspection_rejection_attachments
                                ?.length &&
                                rejectedDetails?.inspection_rejection_attachments?.map(
                                  (src, idx) => (
                                    <AttachmentBox key={idx}>
                                      <LazyImage
                                        src={src?.file?.url}
                                        className="main-img"
                                        previewImage={true}
                                      />
                                    </AttachmentBox>
                                  ),
                                )}
                            </AttachmentStack>
                          </SABox>
                        </SABox>
                        {/* Divider between each rejected detail except last */}
                        {index <
                          productQuantity?.rejected_inspected_products.length -
                            2 && <DividerBox my={2} />}
                      </>
                    ))}
                </SABox>
              )}
            </>
          )}
        </SABox>
      }
    />
  );
};

export default ManufactureProductDetailsModal;
